import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import { WithTooltip } from 'basic-components';
import { Download as DownloadIcon } from 'basic-components/icons';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';

import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/modules/datalake';

import dayjs from 'dayjs';

const Download = ({ value, output = false, provider: providerProp = undefined }) => {
  const dispatch = useDispatch(),
        statuses = useSelector(state => state.datascienceState.creatorState.basicState.statuses),
        selectedNodeId = useSelector(state => state.datascienceState.creatorState.builderState.doubleClickedTask),
        date = useMemo(() => {
          const current = statuses?.find(status => status?.payload?.nodeId === selectedNodeId)
          if(current) {
            return current.startTime;
          }
          return statuses?.[0]?.startTime
        }, [selectedNodeId, statuses]),
        valueParsed = useMemo(() => typeof value === "string" && value.startsWith("{") && CommonService.isJson(value) ? JSON.parse(value) : value, [value]),
        id = useMemo(() => valueParsed?.id ? valueParsed.id : (valueParsed?.name ? undefined : valueParsed), [valueParsed]),
        [searchParams] = useSearchParams(),
        provider = useMemo(() => providerProp !== undefined ? providerProp : searchParams.get("provider") || undefined, [providerProp, searchParams]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withHeader("Target", provider, true)
        , [dispatch, provider]),
        getQueryBuilderWithoutParsing = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withHeader("Target", provider, true)
            .withOnParse(response => Promise.resolve(response))
        , [dispatch, provider]),
        searchQuery = useMemo(() => {
          let query = `groupName=${valueParsed?.groupName}&name=${valueParsed?.name}`
          if(date && !output) {
            query += `&arrivalTime<${dayjs.utc(date).valueOf()}`
          } else if(date && output) {
            query += `&arrivalTime>${dayjs.utc(date).valueOf()}`
          }
          return query;
        }, [date, valueParsed, output]),
        { mutateAsync: downloadFileCall } = filesDatalakeQueries.useDownloadFile({ getQueryBuilder: getQueryBuilderWithoutParsing, newTab: true }),
        { refetch: searchFiles } = filesDatalakeQueries.useSearchFiles({ getQueryBuilder, provider, query: searchQuery, shouldRefetch: false, enabled: false, size: 1 }),
        downloadFile = useCallback(() => {
          if(id) {
            downloadFileCall({ id })
          } else {
            searchFiles()
              .then(response => downloadFileCall({ id: response?.data?.pages?.[0]?.items?.[0]?.fid }))
          }
        }, [id, downloadFileCall, searchFiles])

  return (
    <WithTooltip
        data-place="top"
        short
        text="Download"
    >
      <div 
          className="data-science__pipeline-creator__body__options__resolver__header__controls__control"
          onClick={downloadFile}
      >
        <DownloadIcon
            color="blue-bright"
            height={16}
            width={16}
        />
      </div>
    </WithTooltip>
  )
}

Download.propTypes = {
  provider: PropTypes.string,
  value: PropTypes.any.isRequired,
}

export default Download;
